// « Clients » — the cardex (PMS-CARDEX-BRIEF §6.1 S1–S2).
//
// A SEARCH, never the table: 5 343 clients came across from Vega on
// 2026-09-29, and PostgREST stops at 1 000 rows. Nothing shows until the desk
// types two characters or picks a filter; the database then matches every word
// against name, e-mail, telephones, town and Vega number.
//
// « Sociétés sans SIREN » is D6's readiness report: every B2B client an invoice
// could not yet be issued to once electronic invoicing applies.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PMS_CUSTOMER_TYPE_LABELS_FR,
  type CustomerSubtype,
  type GuestId,
  type GuestSearch,
  type GuestSummary,
} from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms } from './PmsProvider.js';
import { GuestSheetPanel } from './GuestSheetPanel.js';
import { agoLabelFr, fmtDate } from './logic.js';
import { todayParis } from './departureSheet.js';

type Filter = 'all' | 'particulier' | 'societe' | 'missingSiren' | 'consentRequired';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'Tous' },
  { key: 'particulier', label: 'Particuliers' },
  { key: 'societe', label: 'Sociétés' },
  { key: 'missingSiren', label: 'Sociétés sans SIREN' },
  // Pass 2 — loaded ahead of the consent campaign (operator 2026-09-29, D9).
  { key: 'consentRequired', label: 'Consentement requis' },
];

const LIMIT = 50;

export function Clients({ debounceMs = 250 }: { debounceMs?: number }) {
  const pms = usePms();
  const canEdit = useCapability(CAPABILITIES.PMS_GUEST_WRITE);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [hits, setHits] = useState<GuestSummary[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<GuestId | null>(null);
  const [subtypes, setSubtypes] = useState<CustomerSubtype[]>([]);
  const seq = useRef(0);

  useEffect(() => {
    void pms.guests.subtypes().then((r) => {
      if (r.ok) setSubtypes(r.value);
    });
  }, [pms]);

  const run = useCallback(async () => {
    const q = query.trim();
    if (q.length < 2 && filter === 'all') {
      seq.current++;
      setHits([]);
      setSearched(false);
      setError('');
      return;
    }
    const mine = ++seq.current;
    const ask: GuestSearch = {
      query: q,
      limit: LIMIT,
      customerType:
        filter === 'particulier' ? 'particulier' : filter === 'societe' || filter === 'missingSiren' ? 'societe' : null,
      missingSiren: filter === 'missingSiren',
      consentRequired: filter === 'consentRequired',
    };
    setLoading(true);
    const r = await pms.guests.search(ask);
    if (mine !== seq.current) return;
    setLoading(false);
    setSearched(true);
    if (!r.ok) {
      setError(r.error.message);
      setHits([]);
      return;
    }
    setError('');
    setHits(r.value.hits);
    setTruncated(r.value.truncated);
  }, [pms, query, filter]);

  useEffect(() => {
    const t = setTimeout(() => void run(), debounceMs);
    return () => clearTimeout(t);
  }, [run, debounceMs]);

  const today = todayParis(new Date());
  const subtypeLabel = (code: string | null) =>
    code ? (subtypes.find((s) => s.code === code)?.labelFr ?? code) : null;

  return (
    <section aria-label="Clients">
      <div className="pms-head">
        <div>
          <h2>Clients</h2>
          <p className="pms-hint">
            Le cardex : les clients repris de Vega et ceux créés ici. Recherche par nom, e-mail, téléphone, ville ou
            n° Vega.
          </p>
        </div>
      </div>

      <div className="pms-toolbar">
        <div className="pms-chips" role="group" aria-label="Filtrer les clients">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`pms-chip${filter === f.key ? ' is-active' : ''}`}
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="pms-search"
          placeholder="Nom, e-mail, téléphone, ville ou n° Vega"
          aria-label="Rechercher un client"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <p className="pms-warn">⚠ {error}</p>}
      {!searched && !error && (
        <p className="pms-hint">Tapez au moins deux caractères, ou choisissez un filtre.</p>
      )}

      {searched && !error && (
        <div className="pms-table-wrap">
          <table className="pms-table pms-clients" aria-label="Clients trouvés">
            <thead>
              <tr>
                <th>Client</th>
                <th>Type</th>
                <th>Contact</th>
                <th>Ville</th>
                <th>Dernière activité</th>
                <th>SIREN</th>
              </tr>
            </thead>
            <tbody>
              {hits.map((g) => (
                <tr
                  key={g.id}
                  className={`pms-cfg-row${openId === g.id ? ' is-open' : ''}`}
                  onClick={() => setOpenId(g.id)}
                >
                  <td>
                    <button type="button" className="pms-link" onClick={() => setOpenId(g.id)}>
                      <strong>{g.lastName.toUpperCase()}</strong> {g.firstName}
                    </button>
                    {g.sourceVegaRef && <span className="pms-sub"> ({g.sourceVegaRef})</span>}
                    {g.vegaHeld && (
                      <span className="pms-tag" title="Nom et coordonnées tenus à jour depuis Vega jusqu’à la mise en service">
                        Vega
                      </span>
                    )}
                    {g.cardexPass === 2 && (
                      <span
                        className="pms-tag is-consent"
                        title="Vu il y a 3 à 5 ans : sa fiche ne se conserve qu’avec son accord (campagne de consentement à venir)"
                      >
                        Consentement requis
                      </span>
                    )}
                  </td>
                  <td>
                    {PMS_CUSTOMER_TYPE_LABELS_FR[g.customerType]}
                    {subtypeLabel(g.customerSubtype) && (
                      <span className="pms-sub"> · {subtypeLabel(g.customerSubtype)}</span>
                    )}
                  </td>
                  <td>
                    {[g.email, g.mobile ?? g.phone].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td>
                    {g.city ?? '—'}
                    {g.countryCode && g.countryCode !== 'FR' && <span className="pms-sub"> · {g.countryCode}</span>}
                  </td>
                  <td>
                    {g.cardexLastActivity ? (
                      <>
                        {fmtDate(g.cardexLastActivity)}
                        <span className="pms-sub"> · {agoLabelFr(g.cardexLastActivity, today)}</span>
                      </>
                    ) : (
                      <span className="pms-sub">—</span>
                    )}
                  </td>
                  <td>{g.siren ?? (g.customerType === 'societe' ? <span className="pms-sub">à saisir</span> : '')}</td>
                </tr>
              ))}
              {hits.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="pms-empty">
                    Aucun client.
                  </td>
                </tr>
              )}
            </tbody>
            {(truncated || hits.length > 0) && (
              <tfoot>
                <tr>
                  <td colSpan={6} className="pms-sub">
                    {truncated
                      ? `Les ${LIMIT} premiers — précisez la recherche pour voir les autres.`
                      : `${hits.length} client${hits.length > 1 ? 's' : ''}`}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {openId && (
        <GuestSheetPanel
          guests={pms.guests}
          id={openId}
          subtypes={subtypes}
          canEdit={canEdit}
          onClose={() => setOpenId(null)}
          onSaved={() => void run()}
        />
      )}
    </section>
  );
}
